import { useMediaQuery } from '@lib/hooks'
import type {
  ProjectItem,
  SortType,
  Workspace,
} from '@store/reducers/dashboard/types'
import { useState } from 'react'

import { TABLET_LANDSCAPE_MEDIA } from '@/constants/media'
import type { ProjectsView } from './types'
import { readProjectsView, saveProjectsView } from './utils'

type Sorter = (a: ProjectItem, b: ProjectItem) => number

const abc: Sorter = (a, b) => a.title.localeCompare(b.title)
const created: Sorter = (a, b) =>
  new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
const updated: Sorter = (a, b) =>
  new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()

const sorters: Record<SortType, Sorter> = {
  abc,
  created,
  updated,
}

// A pin lifts a card above the rest of its folder; inside each of the two
// groups the sort the user picked still decides the order.
const pinned: Sorter = (a, b) =>
  Number(Boolean(b.pinned)) - Number(Boolean(a.pinned))

export const useProjectsList = (
  folderId: string,
  workspace: Workspace | null
) => {
  const [type, setSort] = useState<SortType>('updated')

  const folder = workspace?.folders?.find((f) => f.id === folderId)

  const projects = [...(folder?.projects || [])].sort(
    (a, b) => pinned(a, b) || sorters[type](a, b)
  )

  return { projects, setSort, sort: type }
}

// The rows are a desktop layout: below tablet-landscape the tile is already a
// compact row with a preview, so the stored choice waits for a wider screen
// and the switcher is not offered at all. A media query in JS rather than CSS because
// the two views are different components, not two styles of one.
export const useProjectsView = () => {
  const [stored, setState] = useState<ProjectsView>(readProjectsView)

  const desktop = useMediaQuery(TABLET_LANDSCAPE_MEDIA)

  const setView = (next: ProjectsView) => {
    saveProjectsView(next)
    setState(next)
  }

  const view: ProjectsView = desktop ? stored : 'grid'

  return { setView, switchable: desktop, view }
}
